import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('laizy_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes('/auth/')) {
      localStorage.removeItem('laizy_token');
      localStorage.removeItem('laizy_user');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

export const dashboardAPI = {
  getKPIs: () => api.get('/dashboard/kpis'),
  getWorkflow: () => api.get('/dashboard/workflow'),
};

export const taxAPI = {
  getUStVA: (period = 'monthly', year) => api.get('/tax/ustva', { params: { period, year } }),
  getUStVADetails: (periodKey) => api.get(`/tax/ustva/${periodKey}/details`),
};

export const contactsAPI = {
  list: () => api.get('/contacts'),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  remove: (id) => api.delete(`/contacts/${id}`),
};

export const invoicesAPI = {
  incoming: {
    list: (params) => api.get('/invoices/incoming', { params }),
    upload: (formData) => api.post('/invoices/incoming/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }),
    confirm: (id, data) => api.put(`/invoices/incoming/${id}/confirm`, data),
    remove: (id) => api.delete(`/invoices/incoming/${id}`),
    toggleReverseCharge: (id, data) => api.put(`/invoices/incoming/${id}/reverse-charge`, data),
    getOriginalUrl: (id) => `${api.defaults.baseURL}/invoices/incoming/${id}/original`,
    getOriginal: (id) => api.get(`/invoices/incoming/${id}/original`, { responseType: 'blob' }),
    assignSupplier: (id, data) => api.put(`/invoices/incoming/${id}/supplier`, data),
    setExpenseCategory: (id, expense_category) => api.put(`/invoices/incoming/${id}/expense-category`, { expense_category }),
    supplierSummary: () => api.get('/invoices/incoming/suppliers/summary'),
  },
  inbox: {
    list: () => api.get('/invoices/inbox'),
    simulate: (formData) => api.post('/invoices/inbox/simulate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }),
    remove: (id) => api.delete(`/invoices/inbox/${id}`),
  },
  outgoing: {
    list: () => api.get('/invoices/outgoing'),
    create: (data) => api.post('/invoices/outgoing', data),
    update: (id, data) => api.put(`/invoices/outgoing/${id}`, data),
    remove: (id) => api.delete(`/invoices/outgoing/${id}`),
    cancel: (id) => api.post(`/invoices/outgoing/${id}/cancel`),
    downloadPdf: (id) => api.get(`/invoices/outgoing/${id}/pdf`, { responseType: 'blob' }),
    downloadXRechnung: (id) => api.get(`/invoices/outgoing/${id}/xrechnung`, { responseType: 'blob' }),
    updateStatus: (id, status) => api.put(`/invoices/outgoing/${id}/status`, { status }),
    sendEmail: (id, data) => api.post(`/invoices/outgoing/${id}/send-email`, data),
    refreshStatuses: () => api.post('/invoices/refresh-statuses'),
  },
};

export const emailAPI = {
  send: (data) => api.post('/email/send', data),
};

export const exportAPI = {
  generate: (data) => api.post('/export/generate', data),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  uploadLogo: (formData) => api.post('/settings/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => api.delete('/settings/logo'),
  getLogoUrl: () => `${api.defaults.baseURL}/settings/logo`,
  fetchLogoBlob: () => api.get('/settings/logo', { responseType: 'blob' }),
  ibanLookup: (iban) => api.get('/banking/iban-lookup', { params: { iban } }),
};

export const quotesAPI = {
  list: () => api.get('/quotes'),
  create: (data) => api.post('/quotes', data),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  remove: (id) => api.delete(`/quotes/${id}`),
  updateStatus: (id, status) => api.put(`/quotes/${id}/status`, { status }),
  downloadPdf: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id, data) => api.post(`/quotes/${id}/send-email`, data),
  convertToInvoice: (id) => api.post(`/quotes/${id}/convert-to-invoice`),
};

export const recurringAPI = {
  list: () => api.get('/recurring-invoices'),
  create: (data) => api.post('/recurring-invoices', data),
  createFromInvoice: (invoiceId, data) => api.post(`/recurring-invoices/from-invoice/${invoiceId}`, data),
  update: (id, data) => api.put(`/recurring-invoices/${id}`, data),
  remove: (id) => api.delete(`/recurring-invoices/${id}`),
  runNow: (id) => api.post(`/recurring-invoices/${id}/run-now`),
  runDue: () => api.post('/recurring-invoices/run-due'),
};

export const taxReturnAPI = {  years: () => api.get('/tax/years'),
  categories: () => api.get('/tax/expense-categories'),
  categorizePending: () => api.post('/tax/categorize-pending'),
  euer: (year, basis = 'ist') => api.get('/tax/euer', { params: { year, basis } }),
  ustAnnual: (year) => api.get('/tax/ust-annual', { params: { year } }),
  ustvaKz: (year, period = 'monthly') => api.get('/tax/ustva-kz', { params: { year, period } }),
  download: (params) => api.get('/tax/export', { params, responseType: 'blob' }),
};

export const auditAPI = {
  list: () => api.get('/audit-log'),
};

export const mollieAPI = {
  status: () => api.get('/integrations/mollie/status'),
  connect: (data) => api.post('/integrations/mollie/connect', data),
  updateSettings: (data) => api.put('/integrations/mollie/settings', data),
  disconnect: () => api.post('/integrations/mollie/disconnect'),
  sync: (days_back = 90) => api.post('/integrations/mollie/sync', { days_back }),
};

export const inboxAPI = {
  info: () => api.get('/inbox/info'),
  regenerate: () => api.post('/inbox/regenerate'),
  list: () => api.get('/invoices/inbox'),
  remove: (id) => api.delete(`/invoices/inbox/${id}`),
};

export default api;
